<script setup>
import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Highlight from '@tiptap/extension-highlight'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'

const props = defineProps({
  modelValue: { type: String, default: '' },
  editable: { type: Boolean, default: true },
  placeholder: { type: String, default: '开始编写...' }
})

const emit = defineEmits(['update:modelValue', 'save'])

const editor = useEditor({
  content: props.modelValue,
  editable: props.editable,
  extensions: [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] }
    }),
    Placeholder.configure({ placeholder: props.placeholder }),
    Highlight,
    TaskList,
    TaskItem.configure({ nested: true }),
    Image,
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader
  ],
  onUpdate({ editor: e }) {
    emit('update:modelValue', e.getHTML())
  }
})

watch(() => props.modelValue, (val) => {
  if (editor.value && val !== editor.value.getHTML()) {
    editor.value.commands.setContent(val, false)
  }
})

watch(() => props.editable, (val) => {
  if (editor.value) editor.value.setEditable(val)
})

const handleSave = () => {
  if (editor.value) {
    emit('save', editor.value.getHTML())
  }
}

// Ctrl+S 保存
const onKeyDown = (e) => {
  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
    e.preventDefault()
    handleSave()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown)
  if (editor.value) editor.value.destroy()
})

const toolbarActions = [
  { icon: 'B', title: '粗体', action: () => editor.value?.chain().focus().toggleBold().run(), isActive: () => editor.value?.isActive('bold') },
  { icon: 'I', title: '斜体', action: () => editor.value?.chain().focus().toggleItalic().run(), isActive: () => editor.value?.isActive('italic') },
  { icon: 'S', title: '删除线', action: () => editor.value?.chain().focus().toggleStrike().run(), isActive: () => editor.value?.isActive('strike') },
  { icon: 'H', title: '高亮', action: () => editor.value?.chain().focus().toggleHighlight().run(), isActive: () => editor.value?.isActive('highlight') },
  { divider: true },
  { icon: 'H1', title: '标题1', action: () => editor.value?.chain().focus().toggleHeading({ level: 1 }).run(), isActive: () => editor.value?.isActive('heading', { level: 1 }) },
  { icon: 'H2', title: '标题2', action: () => editor.value?.chain().focus().toggleHeading({ level: 2 }).run(), isActive: () => editor.value?.isActive('heading', { level: 2 }) },
  { icon: 'H3', title: '标题3', action: () => editor.value?.chain().focus().toggleHeading({ level: 3 }).run(), isActive: () => editor.value?.isActive('heading', { level: 3 }) },
  { divider: true },
  { icon: '•', title: '无序列表', action: () => editor.value?.chain().focus().toggleBulletList().run(), isActive: () => editor.value?.isActive('bulletList') },
  { icon: '1.', title: '有序列表', action: () => editor.value?.chain().focus().toggleOrderedList().run(), isActive: () => editor.value?.isActive('orderedList') },
  { icon: '☑', title: '任务列表', action: () => editor.value?.chain().focus().toggleTaskList().run(), isActive: () => editor.value?.isActive('taskList') },
  { divider: true },
  { icon: '""', title: '引用', action: () => editor.value?.chain().focus().toggleBlockquote().run(), isActive: () => editor.value?.isActive('blockquote') },
  { icon: '<>', title: '代码块', action: () => editor.value?.chain().focus().toggleCodeBlock().run(), isActive: () => editor.value?.isActive('codeBlock') },
  { icon: '—', title: '分割线', action: () => editor.value?.chain().focus().setHorizontalRule().run() },
]
</script>

<template>
  <div class="document-editor">
    <!-- 工具栏 -->
    <div v-if="editable" class="editor-toolbar">
      <template v-for="(item, i) in toolbarActions" :key="i">
        <div v-if="item.divider" class="toolbar-divider" />
        <button
          v-else
          class="toolbar-btn"
          :class="{ 'is-active': item.isActive?.() }"
          :title="item.title"
          @click="item.action"
        >
          {{ item.icon }}
        </button>
      </template>
      <div class="toolbar-spacer" />
      <button class="toolbar-btn save-btn" title="保存 (Ctrl+S)" @click="handleSave">
        💾
      </button>
    </div>
    <!-- 编辑器内容 -->
    <EditorContent :editor="editor" class="editor-content" />
  </div>
</template>

<style scoped>
.document-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.editor-toolbar::-webkit-scrollbar {
  height: 2px;
}

.editor-toolbar::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 1px;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.toolbar-btn:hover {
  background: #e5e7eb;
}

.toolbar-btn.is-active {
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  color: white;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #d1d5db;
  margin: 0 4px;
}

.toolbar-spacer {
  flex: 1;
}

.save-btn {
  font-size: 14px;
}

.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.editor-content :deep(.tiptap) {
  outline: none;
  min-height: 200px;
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
}

.editor-content :deep(.tiptap p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.editor-content :deep(.tiptap h1) { font-size: 1.8em; font-weight: 700; margin: 1em 0 0.5em; }
.editor-content :deep(.tiptap h2) { font-size: 1.4em; font-weight: 600; margin: 0.8em 0 0.4em; }
.editor-content :deep(.tiptap h3) { font-size: 1.2em; font-weight: 600; margin: 0.6em 0 0.3em; }

.editor-content :deep(.tiptap ul),
.editor-content :deep(.tiptap ol) {
  padding-left: 1.5em;
  margin: 0.5em 0;
}

.editor-content :deep(.tiptap blockquote) {
  border-left: 3px solid #0ea5e9;
  padding-left: 1em;
  color: #6b7280;
  margin: 0.5em 0;
}

.editor-content :deep(.tiptap pre) {
  background: #1f2937;
  color: #e5e7eb;
  padding: 12px 16px;
  border-radius: 6px;
  font-family: 'Fira Code', monospace;
  font-size: 13px;
  overflow-x: auto;
}

.editor-content :deep(.tiptap code) {
  background: #f3f4f6;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.9em;
}

.editor-content :deep(.tiptap mark) {
  background: #fef08a;
  padding: 1px 2px;
  border-radius: 2px;
}

.editor-content :deep(.tiptap hr) {
  border: none;
  border-top: 2px solid #e5e7eb;
  margin: 1.5em 0;
}

.editor-content :deep(.tiptap table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5em 0;
}

.editor-content :deep(.tiptap table td),
.editor-content :deep(.tiptap table th) {
  border: 1px solid #d1d5db;
  padding: 8px 12px;
  text-align: left;
}

.editor-content :deep(.tiptap table th) {
  background: #f3f4f6;
  font-weight: 600;
}

.editor-content :deep(.tiptap ul[data-type="taskList"]) {
  list-style: none;
  padding-left: 0;
}

.editor-content :deep(.tiptap ul[data-type="taskList"] li) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.editor-content :deep(.tiptap img) {
  max-width: 100%;
  border-radius: 6px;
}
</style>
